import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Printer, Share2, Download, ArrowLeft, Check, Repeat, X, Loader2, Globe } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function QuoteView() {
  const { id } = useParams();
  const { t, lang, setLang } = useT();
  const [q, setQ] = useState(null);
  const [settings, setSettings] = useState(null);
  const [err, setErr] = useState("");
  const [swapLine, setSwapLine] = useState(null); // { category_id, category_name, line_number, current_device_name }
  // Print language picker — opened via Print button. Defaults to the current i18n language
  // (which itself came from the rep's saved default_language). Rep can override per print
  // so the customer reads the receipt in their preferred language.
  const [printLangOpen, setPrintLangOpen] = useState(false);
  const printRef = useRef(null);
  const viewFired = useRef(false);
  const { user } = useAuth();
  const isAdmin = !!user;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${id}`);
        setQ(data);
        if (!viewFired.current) {
          viewFired.current = true;
          api.post(`/quotes/${id}/view`).catch(() => {});
        }
      } catch (_e) { setErr("Quote not found"); }
    })();
    // Public settings endpoint exposes the print-margin fields.
    api.get(`/settings`).then(({ data }) => setSettings(data)).catch(() => {});
  }, [id]);

  const handlePrint = () => window.print();

  // Print confirmation: if the rep has multiple languages available we prompt them which
  // language the customer wants. Defaults to the rep's saved language. Setting `lang` here
  // re-renders the receipt with the new translations before window.print().
  //
  // Why the timeout is 350ms even when the language hasn't changed: Radix Dialog has a
  // close animation (~150ms) and is portaled to <body>. Calling window.print() while the
  // overlay is mid-fade causes Chrome to capture the dialog instead of the receipt
  // (the customer-reported "blank page with only the language picker" bug). Waiting for
  // the close transition + a paint frame guarantees the receipt is what gets printed.
  const printInLanguage = (chosenLang) => {
    if (chosenLang && chosenLang !== lang) setLang(chosenLang);
    setPrintLangOpen(false);
    setTimeout(() => window.print(), 350);
  };
  const openPrintLangPicker = () => setPrintLangOpen(true);

  const handleShare = async () => {
    const url = window.location.href;
    api.post(`/quotes/${id}/share`).catch(() => {});
    try {
      if (navigator.share) {
        await navigator.share({ title: "TW Calc quote", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Quote link copied!");
      }
    } catch (_e) { /* user cancelled */ }
  };

  const handleDownload = () => {
    if (!q) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const left = 56;
    let y = 64;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("TW Calc — Quote Summary", left, y); y += 22;

    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(`Quote ID: ${q.id}`, left, y); y += 14;
    doc.text(`Generated: ${new Date(q.created_at).toLocaleString()}`, left, y); y += 24;

    if (q.customer_name || q.customer_email) {
      doc.setFont("helvetica", "bold"); doc.text("Customer", left, y); y += 14;
      doc.setFont("helvetica", "normal");
      if (q.customer_name) { doc.text(q.customer_name, left, y); y += 14; }
      if (q.customer_email) { doc.text(q.customer_email, left, y); y += 14; }
      y += 8;
    }

    const hasBillItems = q.bill_items && q.bill_items.length > 0;
    if (hasBillItems) {
      doc.setFont("helvetica", "bold"); doc.text("Bill — by device type", left, y); y += 14;
      doc.setFont("helvetica", "normal");
      // Group by category_name preserving order
      const grouped = {};
      const order = [];
      for (const it of q.bill_items) {
        if (!grouped[it.category_name]) { grouped[it.category_name] = []; order.push(it.category_name); }
        grouped[it.category_name].push(it);
      }
      for (const cat of order) {
        const items = grouped[cat];
        const subtotal = items.reduce((s, x) => s + (x.line_price || 0), 0);
        doc.setFont("helvetica", "bold");
        doc.text(`${cat}  ·  ${items.length} line${items.length > 1 ? "s" : ""}`, left, y);
        doc.text(`$${subtotal.toFixed(2)}/mo`, 540, y, { align: "right" });
        y += 14;
        doc.setFont("helvetica", "normal");
        for (const it of items) {
          doc.text(`   Line ${it.line_number} — ${it.plan_name}`, left, y);
          doc.text(`$${(it.line_price || 0).toFixed(2)}`, 540, y, { align: "right" });
          y += 14;
          if (it.device_name) {
            doc.setFont("helvetica", "italic"); doc.setFontSize(9);
            const parts = [it.device_name];
            if (it.device_scenario_label) parts.push(it.device_scenario_label);
            if (it.device_monthly_price > 0) parts.push(`finance $${it.device_monthly_price.toFixed(2)}/mo`);
            if (it.device_one_time_price > 0) parts.push(`today $${it.device_one_time_price.toFixed(2)}`);
            doc.text(`      ${parts.join(" · ")}`, left, y);
            y += 12;
            doc.setFont("helvetica", "normal"); doc.setFontSize(10);
          }
          if (it.original_price != null && it.discount_label) {
            doc.setFont("helvetica", "italic"); doc.setFontSize(9);
            doc.text(`      was $${it.original_price.toFixed(2)} · ${it.discount_label}`, left, y);
            y += 12;
            doc.setFont("helvetica", "normal"); doc.setFontSize(10);
          }
        }
        y += 4;
      }
      y += 6;
    } else {
      doc.setFont("helvetica", "bold"); doc.text("Plan", left, y); y += 14;
      doc.setFont("helvetica", "normal");
      doc.text(`${q.plan_name}  ·  ${q.lines} line${q.lines > 1 ? "s" : ""}`, left, y); y += 24;
    }

    if (q.device_id && q.device_name) {
      doc.setFont("helvetica", "bold"); doc.text("Phone", left, y); y += 14;
      doc.setFont("helvetica", "normal");
      const path = q.device_scenario_key ? ` (${q.device_scenario_key.replace("_", " ")})` : "";
      doc.text(`${q.device_name}${path}`, left, y); y += 14;
      if (q.device_monthly_price && q.device_monthly_price > 0) {
        doc.text(`Device finance: $${q.device_monthly_price.toFixed(2)}/mo`, left, y); y += 14;
      }
      if (q.device_one_time_price && q.device_one_time_price > 0) {
        doc.text(`Phone today (incl. tax + fee): $${q.device_one_time_price.toFixed(2)}`, left, y); y += 14;
      }
      y += 10;
    }

    doc.setFont("helvetica", "bold"); doc.text("Charges", left, y); y += 14;
    doc.setFont("helvetica", "normal");
    const rows = hasBillItems
      ? [
          ["Plan monthly (sum of all lines)", `$${q.subtotal.toFixed(2)}`],
          ...q.addons.map((a) => {
            const isDiscount = (a.price || 0) < 0;
            return [`${isDiscount ? "−" : "+"} ${a.name}`, `${isDiscount ? "−" : ""}$${Math.abs(a.price || 0).toFixed(2)}`];
          }),
        ]
      : [
          [`Base plan × ${q.lines}`, `$${q.subtotal.toFixed(2)}`],
          ...q.addons.map((a) => [`+ ${a.name} × ${q.lines}`, `$${(a.price * q.lines).toFixed(2)}`]),
        ];
    if (q.device_monthly_price && q.device_monthly_price > 0) {
      rows.push([`Device finance (${q.device_name || "phone"})`, `$${q.device_monthly_price.toFixed(2)}`]);
    }
    if (q.access_charge && q.access_charge > 0) {
      rows.push(["Access charge (phones)", `$${q.access_charge.toFixed(2)}`]);
    }
    if (q.plan_today && q.plan_today > 0) {
      rows.push(["Plan today (multi-month prepay)", `$${q.plan_today.toFixed(2)}`]);
    }
    (q.addons || []).filter((a) => a.charge_first_month_today && (a.price || 0) > 0).forEach((a) => {
      rows.push([`${a.name} (first month today)`, `$${a.price.toFixed(2)}`]);
    });
    (q.addons || []).filter((a) => (a.one_time_price || 0) > 0).forEach((a) => {
      rows.push([`${a.name} (today)`, `$${a.one_time_price.toFixed(2)}`]);
    });
    rows.forEach((r) => {
      doc.text(r[0], left, y);
      doc.text(r[1], 540, y, { align: "right" });
      y += 14;
    });
    y += 6;
    doc.setLineWidth(0.5); doc.line(left, y, 540, y); y += 16;

    // Recompute aggregates the same way the screen does.
    const _billMonthly = (q.bill_items || []).reduce((s, it) => s + (it.device_monthly_price || 0), 0);
    const _billOneTime = (q.bill_items || []).reduce((s, it) => s + (it.device_one_time_price || 0), 0);
    const monthlyDeviceAgg = _billMonthly > 0 ? _billMonthly : (q.device_monthly_price || 0);
    const _addOnsToday = (q.addons || []).reduce((s, a) => {
      const firstMonth = a.charge_first_month_today && (a.price || 0) > 0 ? a.price : 0;
      return s + firstMonth + (a.one_time_price || 0);
    }, 0);
    const dueTodayAgg = (_billOneTime > 0 ? _billOneTime : (q.device_one_time_price || 0))
      + (q.access_charge || 0) + (q.plan_today || 0) + _addOnsToday;
    const monthlyTotal = q.total + monthlyDeviceAgg;
    doc.setFont("helvetica", "bold"); doc.setFontSize(14);
    doc.text("Monthly Total", left, y);
    doc.text(`$${monthlyTotal.toFixed(2)}`, 540, y, { align: "right" });
    y += 18;
    if (dueTodayAgg > 0) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text("Due today", left, y);
      doc.text(`$${dueTodayAgg.toFixed(2)}`, 540, y, { align: "right" });
      y += 18;
    }
    doc.setFont("helvetica", "italic"); doc.setFontSize(9);
    doc.text("Plan taxes & fees included. Device sales tax + phone access charge in 'Due today'.", left, y);
    y += 16;

    doc.setFont("helvetica", "italic"); doc.setFontSize(9);
    doc.text("Estimate only. Taxes & fees vary by location. Not affiliated with Total Wireless / TracFone / Verizon.", left, y, { maxWidth: 484 });

    doc.save(`twcalc-quote-${q.id.slice(0, 8)}.pdf`);
  };

  if (err) return <div className="tw-container py-20 text-slate-500" data-testid="quote-not-found">{err}</div>;
  if (!q) return <div className="tw-container py-20 text-slate-500" data-testid="quote-loading">Loading…</div>;

  // Aggregate per-line device totals (v1.4+); fall back to legacy top-level fields when not present.
  const billDeviceMonthly = (q.bill_items || []).reduce((s, it) => s + (it.device_monthly_price || 0), 0);
  const billDeviceOneTime = (q.bill_items || []).reduce((s, it) => s + (it.device_one_time_price || 0), 0);
  const monthlyDeviceTotal = billDeviceMonthly > 0 ? billDeviceMonthly : (q.device_monthly_price || 0);
  const accessCharge = q.access_charge || 0;
  const planTodayQ = q.plan_today || 0;
  const addOnsTodayQ = (q.addons || []).reduce((s, a) => {
    const firstMonth = a.charge_first_month_today && (a.price || 0) > 0 ? a.price : 0;
    return s + firstMonth + (a.one_time_price || 0);
  }, 0);
  const dueToday = (billDeviceOneTime > 0 ? billDeviceOneTime : (q.device_one_time_price || 0))
    + accessCharge + planTodayQ + addOnsTodayQ;

  const handleSwapped = (updated) => { setQ(updated); setSwapLine(null); toast.success("Device swapped"); };

  return (
    <main className="bg-slate-50 min-h-[80vh] py-12" data-testid="page-quote-view">
      <div className="tw-container max-w-3xl">
        <div className="no-print flex items-center justify-between flex-wrap gap-3 mb-6">
          <Link to="/estimator" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0B132B]" data-testid="quote-back">
            <ArrowLeft className="h-4 w-4" /> {t("new_estimate")}
          </Link>
          <div className="flex gap-3">
            <button onClick={handleShare} className="btn-outline-navy !py-2.5 !px-4 text-sm" data-testid="quote-share">
              <Share2 className="h-4 w-4" /> {t("share")}
            </button>
            <button onClick={handleDownload} className="btn-outline-navy !py-2.5 !px-4 text-sm" data-testid="quote-download">
              <Download className="h-4 w-4" /> {t("pdf")}
            </button>
            <button onClick={isAdmin ? openPrintLangPicker : handlePrint} className="btn-navy !py-2.5 !px-4 text-sm" data-testid="quote-print">
              <Printer className="h-4 w-4" /> {t("print")}
            </button>
          </div>
        </div>

        <div
          ref={printRef}
          className="bg-white border border-slate-200 p-10 lg:p-14"
          data-testid="quote-receipt"
          style={{
            // Print padding is read by index.css `@media print` via these CSS variables.
            // Fall back to 1/0.75/1/1 if the settings call hasn't returned yet.
            "--print-pad-top": `${settings?.print_margin_top_in ?? 1}in`,
            "--print-pad-right": `${settings?.print_margin_right_in ?? 1}in`,
            "--print-pad-bottom": `${settings?.print_margin_bottom_in ?? 0.75}in`,
            "--print-pad-left": `${settings?.print_margin_left_in ?? 1}in`,
          }}
        >
          {/* Store letterhead — printed at the top of the quote so the customer sees the
              actual storefront that produced the estimate. Hidden when no store is attached
              (anonymous public quotes). */}
          {(q.letterhead_store_name || q.letterhead_store_address || q.letterhead_store_phone || q.letterhead_rep_name) && (
            <div className="pb-4 mb-6 border-b-2 border-[#0B132B]" data-testid="quote-letterhead">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  {q.letterhead_store_name && (
                    <div className="text-xl font-extrabold text-[#0B132B] tracking-tight" data-testid="letterhead-store-name">{q.letterhead_store_name}</div>
                  )}
                  {q.letterhead_store_address && (
                    <div className="text-xs text-slate-600 mt-0.5" data-testid="letterhead-store-address">{q.letterhead_store_address}</div>
                  )}
                  {q.letterhead_store_phone && (
                    <div className="mono text-xs text-slate-600 mt-0.5" data-testid="letterhead-store-phone">{q.letterhead_store_phone}</div>
                  )}
                </div>
                {q.letterhead_rep_name && (
                  <div className="text-right">
                    <div className="tw-overline">Prepared by</div>
                    <div className="text-sm font-semibold text-[#0B132B] mt-0.5" data-testid="letterhead-rep-name">{q.letterhead_rep_name}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="tw-overline">{t("quote_summary")}</div>
              <h1 className="mt-2 text-3xl font-extrabold text-[#0B132B] tracking-tight">{t("quote_h1")}</h1>
            </div>
            <div className="text-right">
              <div className="mono text-xs text-slate-500">QUOTE</div>
              <div className="mono text-sm text-[#0B132B]">{q.id.slice(0, 8).toUpperCase()}</div>
              <div className="mono text-xs text-slate-500 mt-2">{new Date(q.created_at).toLocaleDateString()}</div>
            </div>
          </div>

          {(q.customer_name || q.customer_email) && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-200 pt-6">
              <div>
                <div className="tw-overline">{t("quote_bill_to")}</div>
                <div className="mt-1 text-base font-semibold text-[#0B132B]">{q.customer_name || "—"}</div>
                <div className="text-sm text-slate-500">{q.customer_email || ""}</div>
              </div>
            </div>
          )}

          {q.bill_items && q.bill_items.length > 0 ? (
            <div className="mt-8 border-t border-slate-200 pt-6" data-testid="quote-bill-items">
              <div className="tw-overline mb-3">Bill — by device type</div>
              {(() => {
                const grouped = {};
                const order = [];
                for (const it of q.bill_items) {
                  if (!grouped[it.category_name]) { grouped[it.category_name] = []; order.push(it.category_name); }
                  grouped[it.category_name].push(it);
                }
                return order.map((cat) => {
                  const items = grouped[cat];
                  const subtotal = items.reduce((s, x) => s + (x.line_price || 0), 0);
                  return (
                    <div key={cat} className="mb-5" data-testid={`quote-bill-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <div className="text-base font-bold text-[#0B132B]">{cat}</div>
                        <div className="mono text-sm text-slate-500">{items.length} {items.length > 1 ? t("lines") : t("line")}</div>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        {items.map((it, i) => (
                          <div key={i} data-testid={`quote-line-${it.category_id || it.category_name}-${it.line_number}`}>
                            <LineRow
                              muted={i > 0}
                              label={`Line ${it.line_number} — ${it.plan_name}`}
                              value={`$${(it.line_price || 0).toFixed(2)}`}
                            />
                            {it.device_name && (
                              <div className="text-[10px] text-[#0B132B] mono uppercase tracking-widest mt-0.5 ml-1 flex items-center gap-2 flex-wrap" data-testid={`quote-device-${it.category_id || it.category_name}-${it.line_number}`}>
                                <span>
                                  {it.device_name}
                                  {it.device_scenario_label ? ` · ${it.device_scenario_label}` : ""}
                                  {it.device_monthly_price > 0 && ` · finance $${it.device_monthly_price.toFixed(2)}/mo`}
                                  {it.device_one_time_price > 0 && ` · today $${it.device_one_time_price.toFixed(2)}`}
                                </span>
                                {isAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => setSwapLine({ category_id: it.category_id, category_name: it.category_name, line_number: it.line_number, current_device_name: it.device_name })}
                                    className="no-print inline-flex items-center gap-1 text-[#008B99] hover:text-[#00E5FF] normal-case tracking-normal text-[10px] underline"
                                    data-testid={`quote-swap-${it.category_id || it.category_name}-${it.line_number}`}
                                  >
                                    <Repeat className="h-3 w-3" /> swap device
                                  </button>
                                )}
                              </div>
                            )}
                            {it.original_price != null && it.discount_label && (
                              <div className="text-[10px] text-[#008B99] mono uppercase tracking-widest mt-0.5 ml-1" data-testid={`quote-discount-${it.category_id || it.category_name}-${it.line_number}`}>
                                was ${it.original_price.toFixed(2)} · {it.discount_label}
                              </div>
                            )}
                          </div>
                        ))}
                        <div className="flex items-baseline justify-between border-t border-slate-100 pt-1.5">
                          <span className="text-xs uppercase tracking-widest mono text-slate-500">{cat} subtotal</span>
                          <span className="mono ticker-num font-bold text-[#0B132B]">${subtotal.toFixed(2)}/mo</span>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="mt-8 border-t border-slate-200 pt-6">
              <div className="tw-overline">{t("plan")}</div>
              <div className="mt-1 flex items-baseline justify-between">
                <div className="text-xl font-bold text-[#0B132B]">{q.plan_name}</div>
                <div className="mono text-sm text-slate-500">{q.lines} {q.lines > 1 ? t("lines") : t("line")}</div>
              </div>
            </div>
          )}

          {q.device_id && q.device_name && (
            <div className={`mt-6 border-t border-slate-200 pt-6 ${q.bill_items && q.bill_items.length > 0 ? "print-redundant" : ""}`} data-testid="quote-phone-block">
              <div className="tw-overline">Phone</div>
              <div className="mt-1 flex items-baseline justify-between flex-wrap gap-2">
                <div className="text-xl font-bold text-[#0B132B]">{q.device_name}</div>
                {q.device_scenario_key && (
                  <span className="badge-pill badge-teal" data-testid="quote-phone-scenario">
                    {q.device_scenario_key.replace("_", " ")}
                  </span>
                )}
              </div>
              <div className="mt-3 space-y-2 text-sm">
                {q.device_monthly_price > 0 && (
                  <LineRow muted label="Device finance" value={`$${q.device_monthly_price.toFixed(2)}/mo`} />
                )}
                {q.device_one_time_price > 0 && (
                  <LineRow muted label="Phone today (incl. tax + fee)" value={`$${q.device_one_time_price.toFixed(2)}`} />
                )}
              </div>
            </div>
          )}

          <div className="mt-6 border-t border-slate-200 pt-6" data-testid="quote-charges">
            <div className="tw-overline mb-3">Monthly charges</div>
            <div className="space-y-2 text-sm" data-testid="quote-charges-monthly">
              {q.bill_items && q.bill_items.length > 0 ? (
                <LineRow label="Plan monthly (sum of all lines)" value={`$${q.subtotal.toFixed(2)}`} />
              ) : (
                <>
                  <LineRow label={`${t("base_plan")} × ${q.lines}`} value={`$${q.subtotal.toFixed(2)}`} />
                  {q.addons.map((a) => (
                    <LineRow key={a.name} muted label={`+ ${a.name} × ${q.lines}`} value={`$${(a.price * q.lines).toFixed(2)}`} />
                  ))}
                </>
              )}
              {q.bill_items && q.bill_items.length > 0 && q.addons.map((a) => {
                const isDiscount = (a.price || 0) < 0;
                return (
                  <LineRow
                    key={a.name}
                    muted
                    label={`${isDiscount ? "−" : "+"} ${a.name}`}
                    value={`${isDiscount ? "−" : ""}$${Math.abs(a.price || 0).toFixed(2)}`}
                  />
                );
              })}
              {q.device_monthly_price > 0 && (
                <LineRow muted label={`+ Device finance (${q.device_name})`} value={`$${q.device_monthly_price.toFixed(2)}`} />
              )}
            </div>

            {/* Monthly total — shown immediately under the monthly subsection so the
                customer reads "what you owe each month" at a glance. */}
            <div className="mt-4 pt-4 border-t border-slate-300 flex items-baseline justify-between">
              <div>
                <div className="tw-overline">{t("total_month")}</div>
                <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5 no-print">
                  <Check className="h-3 w-3 text-[#008B99]" /> {t("saved_shareable")}
                </div>
              </div>
              <div className="mono text-4xl font-extrabold text-[#0B132B] ticker-num" data-testid="quote-monthly-total">
                ${(q.total + monthlyDeviceTotal).toFixed(2)}
              </div>
            </div>

            {/* Today's charges — anything paid up-front the day the customer activates.
                Heavy divider above this block visually separates "every-month money" from
                "one-time today money". */}
            {(() => {
              const accessCharge = q.access_charge > 0;
              const planToday = q.plan_today > 0;
              const firstMonthAddons = (q.addons || []).filter((a) => a.charge_first_month_today && (a.price || 0) > 0);
              const oneTimeAddons = (q.addons || []).filter((a) => (a.one_time_price || 0) > 0);
              // Show "Device price" and "Sales tax" as separate line items in Today's charges.
              // For new quotes (v1.36+) the breakdown is persisted on each bill_item. For
              // legacy quotes that only stored `device_one_time_price`, we derive the split
              // on-the-fly using the current settings tax rate as the best-available estimate:
              //   • finance lines (device_monthly_price > 0): the entire upfront amount is tax
              //     (the device itself is being financed), so subtotal=0, tax=device_one_time_price.
              //   • non-finance lines: assume retail-basis taxation and back out
              //     subtotal = onetime / (1 + rate), tax = onetime − subtotal.
              const billItems = q.bill_items || [];
              const taxRate = (settings?.device_tax_rate_pct || 0) / 100;
              let deviceSubtotal = 0;
              let deviceTax = 0;
              for (const it of billItems) {
                const onetime = it.device_one_time_price || 0;
                if (onetime <= 0) continue;
                if (it.device_subtotal != null || it.device_tax != null) {
                  deviceSubtotal += it.device_subtotal || 0;
                  deviceTax += it.device_tax || 0;
                } else if ((it.device_monthly_price || 0) > 0) {
                  // Finance line — onetime is pure tax (computed against the financed device).
                  deviceTax += onetime;
                } else if (taxRate > 0) {
                  const sub = onetime / (1 + taxRate);
                  deviceSubtotal += sub;
                  deviceTax += onetime - sub;
                } else {
                  deviceSubtotal += onetime;
                }
              }
              const showDeviceLines = deviceSubtotal > 0 || deviceTax > 0;
              const hasToday =
                accessCharge ||
                planToday ||
                firstMonthAddons.length > 0 ||
                oneTimeAddons.length > 0 ||
                showDeviceLines;
              if (!hasToday) return null;
              return (
                <>
                  <div className="mt-6 border-t-2 border-[#0B132B] pt-5">
                    <div className="tw-overline mb-3">Today's charges</div>
                    <div className="space-y-2 text-sm" data-testid="quote-charges-today">
                      {deviceSubtotal > 0 && (
                        <LineRow muted label="Device price" value={`$${deviceSubtotal.toFixed(2)}`} />
                      )}
                      {deviceTax > 0 && (
                        <LineRow muted label="Sales tax" value={`$${deviceTax.toFixed(2)}`} />
                      )}
                      {accessCharge && <LineRow muted label="Access charge" value={`$${q.access_charge.toFixed(2)}`} />}
                      {planToday && <LineRow muted label="Plan today" value={`$${q.plan_today.toFixed(2)}`} />}
                      {firstMonthAddons.map((a) => (
                        <LineRow key={`tod-${a.name}`} muted label={a.name} value={`$${a.price.toFixed(2)}`} />
                      ))}
                      {oneTimeAddons.map((a) => (
                        <LineRow key={`otp-${a.name}`} muted label={a.name} value={`$${a.one_time_price.toFixed(2)}`} />
                      ))}
                    </div>
                    {dueToday > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-300 flex items-baseline justify-between" data-testid="quote-due-today">
                        <div className="tw-overline">Due today</div>
                        <div className="mono text-3xl font-extrabold text-[#008B99] ticker-num">${dueToday.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            <p className="mt-4 text-xs text-slate-500 italic no-print">Plan taxes & fees are included. Device sales tax + phone access charge are in "Due today".</p>
          </div>

          {/* Scan-to-revisit QR code — encodes the live shareable quote URL so the customer
              can pull this estimate up on their phone after walking out of the store
              (handy for sharing with family or revisiting before activation day). Visible
              on screen AND in print so the printed receipt is genuinely useful. */}
          <div className="mt-8 pt-6 border-t border-slate-200 flex items-center gap-4 flex-wrap" data-testid="quote-qr-block">
            <div className="bg-white p-2 border-2 border-[#0B132B] shrink-0" data-testid="quote-qr">
              <QRCodeSVG
                value={typeof window !== "undefined" ? window.location.href : ""}
                size={88}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#0B132B"
              />
            </div>
            <div className="min-w-0">
              <div className="tw-overline">Scan to revisit</div>
              <div className="mt-1 text-sm font-semibold text-[#0B132B]">Pull this quote up on your phone</div>
              <div className="text-xs text-slate-500 mt-0.5">Share it, compare it, or come back to it before activation day.</div>
            </div>
          </div>

          <p className="mt-10 text-xs text-slate-400 leading-relaxed no-print" data-testid="quote-disclaimer">
            {t("quote_disclaimer")}
          </p>
        </div>
      </div>
      {swapLine && (
        <SwapDeviceDialog
          quoteId={q.id}
          line={swapLine}
          onClose={() => setSwapLine(null)}
          onSwapped={handleSwapped}
        />
      )}

      {/* Per-print language picker. Lets the rep override the receipt language for this
          customer without changing their personal default_language. */}
      <Dialog open={printLangOpen} onOpenChange={(v) => { if (!v) setPrintLangOpen(false); }}>
        <DialogContent className="rounded-none max-w-sm" data-testid="print-lang-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#0B132B] flex items-center gap-2">
              <Globe className="h-5 w-5 text-[#00E5FF]" /> Print language
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 -mt-1">Pick the language the customer reads.</p>
          <div className="grid grid-cols-3 gap-2 pt-1">
            {[
              { value: "en", label: "English" },
              { value: "es", label: "Español" },
              { value: "ar", label: "العربية" },
            ].map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => printInLanguage(opt.value)}
                className={`border-2 px-3 py-3 text-sm font-bold tracking-wide ${
                  lang === opt.value
                    ? "border-[#0B132B] bg-[#0B132B] text-white"
                    : "border-slate-300 text-slate-600 hover:border-[#0B132B]"
                }`}
                data-testid={`print-lang-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function SwapDeviceDialog({ quoteId, line, onClose, onSwapped }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickedId, setPickedId] = useState(null);
  const [oneTime, setOneTime] = useState("");
  const [monthly, setMonthly] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/devices", { params: { active_only: true } });
        const filtered = line.category_id
          ? (data || []).filter((d) => d.category_id === line.category_id)
          : (data || []);
        setDevices(filtered);
      } finally { setLoading(false); }
    })();
  }, [line.category_id]);

  const picked = devices.find((d) => d.id === pickedId);

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const body = {
        category_id: line.category_id || null,
        line_number: line.line_number,
        device_id: picked.id,
        device_name: `${picked.brand} ${picked.model}`,
        device_scenario_key: null,
        device_scenario_label: null,
        device_one_time_price: oneTime === "" ? null : parseFloat(oneTime),
        device_monthly_price: monthly === "" ? null : parseFloat(monthly),
      };
      const { data } = await api.post(`/quotes/${quoteId}/swap-device`, body);
      onSwapped(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not swap device");
    } finally { setSaving(false); }
  };

  return (
    <div className="no-print fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="swap-device-dialog" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto border-2 border-[#0B132B] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="tw-overline">Swap device · Line {line.line_number}</div>
            <h2 className="mt-1 text-xl font-bold text-[#0B132B]">{line.category_name}</h2>
            <p className="text-xs text-slate-500 mt-1">Currently: {line.current_device_name}</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center hover:bg-slate-100" data-testid="swap-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
          ) : devices.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No active devices configured in this category.</p>
          ) : (
            <>
              <div className="tw-overline mb-2">Pick a replacement device</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[40vh] overflow-auto">
                {devices.map((d) => {
                  const sel = pickedId === d.id;
                  return (
                    <button
                      key={d.id} type="button"
                      onClick={() => setPickedId(d.id)}
                      className={`text-left p-3 border-2 transition ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
                      data-testid={`swap-pick-${d.id}`}
                    >
                      <div className="tw-overline">{d.brand}</div>
                      <div className="font-bold text-[#0B132B] text-sm truncate">{d.model}</div>
                      <div className="mono text-xs text-slate-500 mt-0.5">MSRP ${(d.msrp || 0).toFixed(2)}</div>
                    </button>
                  );
                })}
              </div>

              {picked && (
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-slate-200 pt-4">
                  <label className="block">
                    <span className="tw-overline">Due today ($)</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={oneTime} onChange={(e) => setOneTime(e.target.value)}
                      placeholder="0.00"
                      className="input-sharp mt-1"
                      data-testid="swap-one-time"
                    />
                  </label>
                  <label className="block">
                    <span className="tw-overline">Finance ($/mo)</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={monthly} onChange={(e) => setMonthly(e.target.value)}
                      placeholder="0.00"
                      className="input-sharp mt-1"
                      data-testid="swap-monthly"
                    />
                  </label>
                  <p className="sm:col-span-2 text-xs text-slate-500">Leave a field blank to clear it on the saved line. Default keeps the existing pricing only if you fill them in.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="btn-outline-navy" data-testid="swap-cancel">Cancel</button>
          <button onClick={save} disabled={!picked || saving} className="btn-brand disabled:opacity-50" data-testid="swap-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />} Swap
          </button>
        </div>
      </div>
    </div>
  );
}

function LineRow({ label, value, muted }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={muted ? "text-slate-500" : "text-slate-700"}>{label}</span>
      <span className={`mono ticker-num ${muted ? "text-slate-500" : "text-[#0B132B] font-semibold"}`}>{value}</span>
    </div>
  );
}
